import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  InputAdornment
} from '@mui/material';
import { useData } from '../../contexts/DataContext';

const CATEGORIES = [
  'Materials',
  'Tools',
  'Equipment',
  'Supplies',
  'Parts',
  'Other'
];

export default function InventoryForm({ open, onClose, item }) {
  const { addInventoryItem, updateInventoryItem } = useData();
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category: '',
    totalQuantity: 0,
    quantity: 0, // For backward compatibility
    unit: '',
    unitPrice: 0,
    lowStockThreshold: 10,
    location: '',
    supplier: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        sku: item.sku || '',
        description: item.description || '',
        category: item.category || '',
        totalQuantity: item.totalQuantity || item.quantity || 0,
        quantity: item.totalQuantity || item.quantity || 0, // For backward compatibility
        unit: item.unit || '',
        unitPrice: item.unitPrice || 0,
        lowStockThreshold: item.lowStockThreshold || 10,
        location: item.location || '',
        supplier: item.supplier || '',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        name: '',
        sku: '',
        description: '',
        category: '',
        totalQuantity: 0,
        quantity: 0,
        unit: '',
        unitPrice: 0,
        lowStockThreshold: 10,
        location: '',
        supplier: '',
        notes: ''
      });
    }
    setErrors({});
  }, [item, open]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (formData.totalQuantity < 0) newErrors.totalQuantity = 'Total quantity cannot be negative';
    if (formData.unitPrice < 0) newErrors.unitPrice = 'Unit price cannot be negative';
    if (formData.lowStockThreshold < 0) newErrors.lowStockThreshold = 'Low stock threshold cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const dataToSave = {
        ...formData,
        quantity: formData.totalQuantity // For backward compatibility
      };
      if (item) {
        await updateInventoryItem(item.id, dataToSave);
      } else {
        await addInventoryItem(dataToSave);
      }
      onClose();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      // Show more specific error message
      const errorMessage = error.message || 'Failed to save inventory item. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{item ? 'Edit Inventory Item' : 'Add Inventory Item'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                value={formData.sku}
                onChange={handleChange('sku')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange('category')}
                  label="Category *"
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit (e.g., pcs, lbs, ft)"
                value={formData.unit}
                onChange={handleChange('unit')}
                placeholder="pcs"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Total Quantity"
                type="number"
                value={formData.totalQuantity}
                onChange={handleChange('totalQuantity')}
                error={!!errors.totalQuantity}
                helperText={errors.totalQuantity || 'Total quantity in inventory'}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={formData.unitPrice}
                onChange={handleChange('unitPrice')}
                error={!!errors.unitPrice}
                helperText={errors.unitPrice}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Low Stock Threshold"
                type="number"
                value={formData.lowStockThreshold}
                onChange={handleChange('lowStockThreshold')}
                error={!!errors.lowStockThreshold}
                helperText={errors.lowStockThreshold}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleChange('location')}
                placeholder="Warehouse A, Shelf 3"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier"
                value={formData.supplier}
                onChange={handleChange('supplier')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {item ? 'Update' : 'Add'} Item
        </Button>
      </DialogActions>
    </Dialog>
  );
}

